import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import {
    Package,
    Search,
    Wrench,
    Clock,
    CheckCircle,
    Truck
} from 'lucide-react';

const statusConfig = {
    ricevuto: {
        icon: Package,
        label: 'Ricevuto',
        color: 'bg-blue-500',
        textColor: 'text-blue-600'
    },
    diagnosi: {
        icon: Search,
        label: 'In Diagnosi',
        color: 'bg-yellow-500',
        textColor: 'text-yellow-600'
    },
    riparazione: {
        icon: Wrench,
        label: 'In Riparazione',
        color: 'bg-orange-500',
        textColor: 'text-orange-600'
    },
    attesa_ricambi: {
        icon: Clock,
        label: 'Attesa Ricambi',
        color: 'bg-purple-500',
        textColor: 'text-purple-600'
    },
    completato: {
        icon: CheckCircle,
        label: 'Completato',
        color: 'bg-green-500',
        textColor: 'text-green-600'
    },
    consegnato: {
        icon: Truck,
        label: 'Consegnato',
        color: 'bg-emerald-500',
        textColor: 'text-emerald-600'
    }
};

const statusOrder = ['ricevuto', 'diagnosi', 'riparazione', 'attesa_ricambi', 'completato', 'consegnato'];

export default function RepairTimeline({ currentStatus, statiRiparazione = [] }) {
    const currentIndex = statusOrder.indexOf(currentStatus);

    // Skip attesa_ricambi if not in the stati
    const hasAttesaRicambi = statiRiparazione.some(s => s.stato === 'attesa_ricambi');
    const displayStatuses = statusOrder.filter(s => s !== 'attesa_ricambi' || hasAttesaRicambi);

    return (
        <div className="relative">
            {/* Desktop Timeline */}
            <div className="hidden md:flex items-center justify-between">
                {displayStatuses.map((status, index) => {
                    const config = statusConfig[status];
                    const Icon = config.icon;
                    const statusIndex = statusOrder.indexOf(status);
                    const isCompleted = statusIndex < currentIndex;
                    const isCurrent = status === currentStatus;
                    const statoData = statiRiparazione.find(s => s.stato === status);

                    return (
                        <React.Fragment key={status}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="flex flex-col items-center relative"
                            >
                                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center
                  ${isCompleted || isCurrent ? config.color : 'bg-gray-200'}
                  ${isCurrent ? 'ring-4 ring-offset-2 ring-blue-200' : ''}
                  transition-all duration-300
                `}>
                                    <Icon className={`w-6 h-6 ${isCompleted || isCurrent ? 'text-white' : 'text-gray-400'}`} />
                                </div>
                                <span className={`
                  mt-2 text-sm font-medium
                  ${isCurrent ? config.textColor : isCompleted ? 'text-gray-900' : 'text-gray-400'}
                `}>
                                    {config.label}
                                </span>
                                {statoData?.created_at && (
                                    <span className="text-xs text-gray-500 mt-1">
                                        {format(new Date(statoData.created_at), 'dd MMM', { locale: it })}
                                    </span>
                                )}
                            </motion.div>

                            {index < displayStatuses.length - 1 && (
                                <div className="flex-1 h-1 mx-2">
                                    <div className={`
                    h-full rounded-full transition-all duration-500
                    ${statusOrder.indexOf(displayStatuses[index + 1]) <= currentIndex ? 'bg-blue-500' : 'bg-gray-200'}
                  `} />
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>

            {/* Mobile Timeline */}
            <div className="md:hidden space-y-4">
                {displayStatuses.map((status, index) => {
                    const config = statusConfig[status];
                    const Icon = config.icon;
                    const statusIndex = statusOrder.indexOf(status);
                    const isCompleted = statusIndex < currentIndex;
                    const isCurrent = status === currentStatus;
                    const statoData = statiRiparazione.find(s => s.stato === status);

                    return (
                        <motion.div
                            key={status}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="flex items-start gap-4"
                        >
                            <div className="flex flex-col items-center">
                                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center shrink-0
                  ${isCompleted || isCurrent ? config.color : 'bg-gray-200'}
                  ${isCurrent ? 'ring-4 ring-offset-2 ring-blue-200' : ''}
                `}>
                                    <Icon className={`w-5 h-5 ${isCompleted || isCurrent ? 'text-white' : 'text-gray-400'}`} />
                                </div>
                                {index < displayStatuses.length - 1 && (
                                    <div className={`w-0.5 h-8 mt-2 ${isCompleted ? 'bg-blue-500' : 'bg-gray-200'}`} />
                                )}
                            </div>
                            <div className="pt-1">
                                <span className={`
                  font-medium
                  ${isCurrent ? config.textColor : isCompleted ? 'text-gray-900' : 'text-gray-400'}
                `}>
                                    {config.label}
                                </span>
                                {statoData?.created_at && (
                                    <p className="text-xs text-gray-500">
                                        {format(new Date(statoData.created_at), "dd MMMM yyyy, HH:mm", { locale: it })}
                                    </p>
                                )}
                                {statoData?.note && (
                                    <p className="text-sm text-gray-600 mt-1">{statoData.note}</p>
                                )}
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
}